import { useState } from "react"
import { t } from "translations"

import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import Typography from "@mui/material/Typography"
import { styled, useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"

import {
    INewsletterCard,
    INewsletterInterestsCategories,
    INewsletterMergeField,
    IPopupBackground
} from "data/contentData/interfaces/mongodb/INewsletter"
import { setNewsletterSubscription } from "data/internal/api/newsletter"

import Icon from "Components/Icon/Icon"
import ContentImage from "Components/Image/ContentImage"

import NewsletterSelect, { SelectOption } from "./NewsletterSelect"
import NewsletterTextFieldWithButton from "./NewsletterTextFieldWithButton"
import {
    IFormValues,
    clearAllFields,
    extractCategories,
    extractMergeFields,
    getInitialFormValues,
    getInterestRenderValue,
    getMergefieldsRenderValue,
    getNewsletterPalette,
    handleEmailChange,
    handleSelectChange,
    handleSignUp
} from "./utils"

export default function Newsletter({
    data,
    popupBackground,
    placementId
}: {
    data: INewsletterCard
    popupBackground?: IPopupBackground
    placementId?: string
}) {
    const initialFormValues = getInitialFormValues(data.newsletterId)

    const [form, setForm] = useState<IFormValues>(initialFormValues)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [isSending, setIsSending] = useState(false)

    const [successfullySubscribed, setSuccessfullySubscribed] = useState<
        undefined | boolean
    >(undefined)

    const theme = useTheme()

    const newsletterPalette = getNewsletterPalette(data.formId, theme)

    const isMdUp = useMediaQuery(theme.breakpoints.up("md"))
    const showAsFullWidth = isMdUp

    if (!data) return null

    const categories = extractCategories(data)
    const mergeFields = extractMergeFields(data)

    return (
        <StyledWrapperFullwidth
            palette={newsletterPalette}
            isfullwidth={showAsFullWidth.toString()}
            popupBackground={popupBackground}
            hasContentImage={data.image ? "true" : "false"}>
            {data.image && data.showAsPopup === false && (
                <StyledContentImage image={data.image} />
            )}

            <StyledDiv>
                <StyledHeading
                    variant="h2"
                    color="inherit">
                    {data.title}
                </StyledHeading>
                {data.text && (
                    <Typography
                        variant="body2"
                        color="inherit">
                        {data.text}
                    </Typography>
                )}

                {data.more && data.more.href && (
                    <StyledButton
                        component="a"
                        href={data.more.href}
                        endIcon={<Icon name="arrowForward" />}
                        variant="text">
                        {data.more.text}
                    </StyledButton>
                )}
            </StyledDiv>

            <StyledForm
                id={data.formId} // for tracking purposes
                data-newsletter-id={data.newsletterId} // for tracking purposes
                data-placement-id={placementId} // for tracking purposes
                component="form"
                fullWidth
                onSubmit={e =>
                    handleSignUp(
                        data.newsletterId,
                        data.formId,
                        e,
                        emailError,
                        form,
                        setIsSending,
                        setSuccessfullySubscribed,
                        setNewsletterSubscription
                    )
                }>
                {successfullySubscribed !== undefined ? (
                    <StyledSuccessText
                        variant={"h3"}
                        sucsessfullysubscribed={successfullySubscribed.toString()}
                        component="p">
                        {successfullySubscribed
                            ? `${t.newsletter.success}`
                            : `${t.newsletter.error}`}

                        <StyledResetButton
                            onClick={() =>
                                clearAllFields(
                                    setForm,
                                    initialFormValues,
                                    setEmailError,
                                    setSuccessfullySubscribed
                                )
                            }
                            fullWidth
                            variant="contained">
                            {t.newsletter.resetForm}
                        </StyledResetButton>
                    </StyledSuccessText>
                ) : (
                    <>
                        {categories &&
                            categories.map(
                                (item: INewsletterInterestsCategories) => {
                                    return (
                                        <NewsletterSelect
                                            type="interests"
                                            name={item.title}
                                            required={item.required}
                                            label={item.title}
                                            options={
                                                item.interests.map(
                                                    (
                                                        interest: SelectOption
                                                    ) => {
                                                        return {
                                                            id: interest.id,
                                                            name: interest.name
                                                        }
                                                    }
                                                ) as SelectOption[]
                                            }
                                            selectedValue={getInterestRenderValue(
                                                item.title,
                                                form
                                            )}
                                            handleChange={event =>
                                                handleSelectChange(
                                                    event,
                                                    "interests",
                                                    form,
                                                    setForm
                                                )
                                            }
                                            key={item.title}
                                            palette={newsletterPalette}
                                        />
                                    )
                                }
                            )}

                        {mergeFields &&
                            mergeFields.map((item: INewsletterMergeField) => {
                                return (
                                    <NewsletterSelect
                                        type="mergefields"
                                        name={item.name}
                                        required={item.required}
                                        label={item.name}
                                        options={item.options}
                                        selectedValue={getMergefieldsRenderValue(
                                            item.tag,
                                            form
                                        )}
                                        handleChange={event =>
                                            handleSelectChange(
                                                event,
                                                "mergefields",
                                                form,
                                                setForm,
                                                item.tag
                                            )
                                        }
                                        key={item.tag}
                                        palette={newsletterPalette}
                                    />
                                )
                            })}

                        <NewsletterTextFieldWithButton
                            onInvalid={e => {
                                e.preventDefault()
                                setEmailError(true)
                            }}
                            emailError={emailError}
                            fullWidth={showAsFullWidth}
                            palette={newsletterPalette}
                            email={form.email}
                            handleSetEmail={input =>
                                handleEmailChange(
                                    input,
                                    form,
                                    setForm,
                                    setEmailError
                                )
                            }
                            isSending={isSending}
                        />
                    </>
                )}
            </StyledForm>
        </StyledWrapperFullwidth>
    )
}

const StyledWrapperFullwidth = styled("div")<{
    palette: any
    isfullwidth: string
    popupBackground?: IPopupBackground
    hasContentImage: string
}>(({ theme, palette, isfullwidth, popupBackground, hasContentImage }) => ({
    ...(popupBackground === undefined
        ? {
              backgroundColor: `${palette.main}`
          }
        : {
              backgroundImage: `url(${popupBackground.src})`,
              backgroundSize: "cover"
          }),
    display:
        isfullwidth === "true" ||
        (isfullwidth === "true" && hasContentImage === "true")
            ? "grid"
            : "flex",
    flexDirection: "column",
    gridTemplateColumns:
        isfullwidth === "true" ||
        (isfullwidth === "true" && hasContentImage === "true")
            ? "1fr 1fr"
            : "unset",

    gridAutoRows: "min-content",
    gap: isfullwidth === "true" ? theme.spacing(4) : theme.spacing(2),
    overflow: "hidden",
    color: `${palette.contrastText}`,
    padding: isfullwidth === "true" ? theme.spacing(3, 4) : theme.spacing(2)
}))

const StyledContentImage = styled(ContentImage)({
    gridColumn: "1",
    gridRow: "span 2"
})

const StyledDiv = styled("div")({
    display: "flex",
    flexDirection: "column"
})

const StyledHeading = styled(Typography)(({ theme }) => ({
    "&.MuiTypography-h2": {
        fontSize: "2rem"
    },
    marginBottom: theme.spacing(0.5)
}))

const StyledButton = styled(Button)<{ component: string }>(({ theme }) => ({
    marginLeft: theme.spacing(-1),
    marginRight: "auto",
    marginTop: "auto",
    marginBottom: theme.spacing(-1),
    "&:hover": {
        backgroundColor: "unset",
        textDecoration: "underline"
    }
}))

const StyledForm = styled(FormControl)<{ component: string }>(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    justifyContent: "center",
    "& input::placeholder": {
        color: theme.palette.text.primary,
        opacity: 1
    }
}))

const StyledSuccessText = styled(Typography)<{
    component: string
    sucsessfullysubscribed: string
}>(({ sucsessfullysubscribed, theme }) => ({
    textAlign: "center",
    color:
        sucsessfullysubscribed === "true"
            ? theme.palette.text.primary
            : theme.palette.error.main
}))

const StyledResetButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
        backgroundColor: theme.palette.primary.light
    },
    "&:focus": {
        outlineOffset: "-2px"
    }
}))
